import { gql, useMutation, useQuery } from '@apollo/client';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  OrganizationUserEntityRelationTypes, User, OrganizationUser, OrganizationUserEntity, Account, AccountStates,
} from 'interfaces';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { entityName } from '../../../../../util';
import {
  Box, Grid, Icon, Typography,
} from '../../../../1-primative';
import {
  Button, Dialog, Form, MenuItem, SelectField, TextField, Switch,
} from '../../../../2-component';
import { RoleSelect, MultiAccountSelect, ACCOUNT_STATES } from '../../../../3-pattern';

interface OrgUserAccess {
  relation?: OrganizationUserEntityRelationTypes,
  roleId?: string,
  allowAccessToSpecificAccounts?: boolean,
  selectedAccountIds?: string[],
}

const FETCH_ORGANIZATION_USER = gql`
  query fetchOrganizationUser($organizationUserId: ObjectID!) {
    fetchOrganizationUser(organizationUserId: $organizationUserId) {
      organizationUser {
        id
        entities {
          relation
          readOnly
          entity { id }
          role { id }
          accounts { id }
        }
      }
    }
  }
`;

const UPDATE_ORGANIZATION_USER = gql`
  mutation updateOrganizationUser($input: UpdateOrganizationUserInput!) {
    updateOrganizationUser(input: $input) {
      organizationUser { id }
    }
  }
`;

export const EditAccessToEntity = ({
  entity, open, onClose, activeOrganizationUser, refetch, limitedAccess,
}: { entity?: User, open: boolean, onClose: () => void, activeOrganizationUser?: OrganizationUser, refetch: () => void, limitedAccess?: boolean }) => {
  const { t } = useTranslation(['components']);
  const [focused, setFocused] = useState<string[]>([]);
  const [orgUserAccess, setOrgUserAccess] = useState<OrgUserAccess>({});

  const { data: userData, loading } = useQuery(FETCH_ORGANIZATION_USER, {
    variables: { organizationUserId: activeOrganizationUser?.id },
    skip: !activeOrganizationUser?.id,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const selectedEntity = data?.fetchOrganizationUser?.organizationUser?.entities.find((e: OrganizationUserEntity) => e.entity?.id === entity?.id);
      const hasSelectedAccounts = (selectedEntity?.accounts?.length ?? 0) > 0;

      setOrgUserAccess({
        relation: selectedEntity?.relation,
        roleId: selectedEntity?.role?.id,
        selectedAccountIds: hasSelectedAccounts ? selectedEntity?.accounts?.map((account: Account) => account.id) : undefined,
        allowAccessToSpecificAccounts: hasSelectedAccounts,
      });
    },
  });

  const [updateOrganizationUser] = useMutation(UPDATE_ORGANIZATION_USER, {
    variables: {
      input: {
        organizationUserId: activeOrganizationUser?.id,
        entities: userData?.fetchOrganizationUser?.organizationUser?.entities?.map((e: OrganizationUserEntity) => {
          if (e.entity?.id !== entity?.id) {
            return {
              entityId: e.entity?.id, roleId: e.role?.id, relation: e.relation, readOnly: e.readOnly, accountIds: e.accounts?.map((a) => a.id),
            };
          }

          return {
            entityId: entity?.id, readOnly: false, roleId: orgUserAccess.roleId, relation: orgUserAccess.relation, accountIds: orgUserAccess.selectedAccountIds,
          };
        }),
      },
    },
    onCompleted: () => {
      handleClose();
      refetch();
    },
  });

  const handleClose = () => {
    onClose();
    setOrgUserAccess({});
    setFocused([]);
  };

  const handleSubmit = () => {
    if (orgUserAccess?.allowAccessToSpecificAccounts && orgUserAccess?.selectedAccountIds?.length === 0) {
      setFocused([...focused, 'accountSelect']);
      return;
    }
    updateOrganizationUser();
  };

  return (
    <Dialog fullWidth open={open} onClose={handleClose} sx={{ '.MuiDialog-container': { '.MuiPaper-root': { padding: '20px' } } }}>
      <Box display="flex" flexDirection="row" justifyContent="end">
        <Box sx={{ cursor: 'pointer' }} display="flex" onClick={handleClose}><Icon icon={CloseRoundedIcon} size='medium' /></Box>
      </Box>
      <Form onSubmit={handleSubmit}>
        <Grid container gap={2}>
          <Grid item xs={12}>
            <Typography variant='headingMedium' sx={{ mt: 1 }}>{t('components:accessToEntity.editButton.title', { entityName: entityName(entity) })}</Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth disabled
              testId='access-user-search'
              label={t('components:accessToEntity.addButton.orgUserSearchText')}
              value={`${activeOrganizationUser?.firstName ?? ''} ${activeOrganizationUser?.lastName ?? ''}`}
            />
          </Grid>
          <Grid item xs={12}>
            <SelectField fullWidth
              testId='access-relation-select'
              label={t('components:accessToEntity.addButton.relationLabel')}
              value={orgUserAccess.relation || ''}
              onChange={(e: any) => setOrgUserAccess((prev) => ({ ...prev, relation: e.target.value }))}
            >
              {Object.values(OrganizationUserEntityRelationTypes).map((relation) => (
                <MenuItem key={relation} value={relation}>{t(`components:accessToEntity.relations.${relation}`)}</MenuItem>
              ))}
            </SelectField>
          </Grid>
          {limitedAccess && (
            <>
              <Grid item xs={12}>
                <RoleSelect includeEmpty
                  testId='access-role-select'
                  value={orgUserAccess.roleId || ''}
                  label={t('components:accessToEntity.addButton.createTab.roleToAccessEntity')}
                  infoTooltip={t('components:accessToEntity.addButton.createTab.noRoleSelected')}
                  onChange={(e: any) => setOrgUserAccess((prev) => ({ ...prev, roleId: e.target.value || undefined }))}
                />
              </Grid>
              <Grid item xs={12}>
                <Switch
                  testId='allow-access-to-specific-accounts'
                  checked={orgUserAccess.allowAccessToSpecificAccounts}
                  label={t('components:accessToEntity.addButton.allowAccessToSpecificAccounts')}
                  onChange={(checked) => {
                    setOrgUserAccess((prev) => ({ ...prev, allowAccessToSpecificAccounts: checked, selectedAccountIds: checked ? [] : undefined }));
                    if (!checked) setFocused([]);
                  }}
                />
              </Grid>
              {entity?.id && orgUserAccess?.allowAccessToSpecificAccounts && (
                <Grid item xs={12}>
                  <MultiAccountSelect
                    testId='access-multi-account-select'
                    forId={entity?.id || ''}
                    label={t('components:accessToEntity.addButton.accounts')}
                    selectedAccountIds={orgUserAccess.selectedAccountIds}
                    states={[...ACCOUNT_STATES, AccountStates.CANCELED, AccountStates.INACTIVE]}
                    onChange={(value: any) => setOrgUserAccess((prev) => ({ ...prev, selectedAccountIds: value }))}
                    error={!orgUserAccess?.selectedAccountIds?.length && focused.includes('accountSelect')}
                    errorText={t('components:accessToEntity.addButton.noAccountsSelectedError')}
                    onBlur={() => setFocused([...focused, 'accountSelect'])}
                  />
                </Grid>
              )}
            </>
          )}
          <Grid item xs={12} mt={3} justifyContent='end' display='flex'>
            <Button dataTestId='grant-access-button' type='submit' color='primary' label={t('components:accessToEntity.editButton.update')} disabled={loading || !activeOrganizationUser} />
          </Grid>
        </Grid>
      </Form>
    </Dialog>
  );
};
